/**
 * BADGE COMPONENT - Apple Liquid Glass Design System
 * macOS Tahoe 26.2 & iOS 26.2
 * Based on WWDC25: Meet Liquid Glass
 *
 * Features:
 * - Glass badge with color tinting
 * - Multiple color variants
 * - Size options
 * - Icon support
 * - Notification dot variant
 */

import { ReactNode } from 'react'
import { cn } from '../../lib/utils'

type BadgeVariant = 'default' | 'blue' | 'green' | 'red' | 'orange' | 'purple' | 'pink' | 'teal' | 'yellow' | 'neutral' | 'gray'
type BadgeSize = 'sm' | 'md' | 'lg'

interface BadgeProps {
  children: ReactNode
  variant?: BadgeVariant
  size?: BadgeSize
  icon?: ReactNode
  dot?: boolean
  className?: string
}

const variantClasses: Record<BadgeVariant, string> = {
  default: 'liquid-badge-glass text-light-text-secondary dark:text-dark-text-secondary',
  blue: 'liquid-badge-glass liquid-badge-blue',
  green: 'liquid-badge-glass liquid-badge-green',
  red: 'liquid-badge-glass liquid-badge-red',
  orange: 'liquid-badge-glass liquid-badge-orange',
  purple: 'liquid-badge-glass liquid-badge-purple',
  pink: 'bg-pink-light dark:bg-pink-glass text-pink dark:text-pink-dark border border-pink/20',
  teal: 'bg-teal-light dark:bg-teal-glass text-teal dark:text-teal-dark border border-teal/20',
  yellow: 'bg-yellow-light dark:bg-yellow-glass text-yellow dark:text-yellow-dark border border-yellow/20',
  neutral: 'liquid-badge-glass text-light-text-secondary dark:text-dark-text-secondary',
  gray: 'liquid-badge-glass text-light-text-tertiary dark:text-dark-text-tertiary',
}

const sizeClasses: Record<BadgeSize, string> = {
  sm: 'px-2 py-0.5 text-caption2 gap-1',
  md: 'px-2.5 py-1 text-footnote gap-1.5',
  lg: 'px-3 py-1.5 text-subheadline gap-2',
}

const dotSizeClasses: Record<BadgeSize, string> = {
  sm: 'w-1.5 h-1.5',
  md: 'w-2 h-2',
  lg: 'w-2.5 h-2.5',
}

export function Badge({
  children,
  variant = 'default',
  size = 'md',
  icon,
  dot = false,
  className,
}: BadgeProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center font-semibold rounded-full',
        'backdrop-filter backdrop-blur-lg',
        variantClasses[variant],
        sizeClasses[size],
        className
      )}
    >
      {/* Dot indicator */}
      {dot && (
        <span
          className={cn(
            'rounded-full bg-current opacity-80',
            dotSizeClasses[size]
          )}
          aria-hidden="true"
        />
      )}

      {/* Icon */}
      {icon && !dot && (
        <span className="flex-shrink-0">
          {icon}
        </span>
      )}

      {/* Content */}
      <span className="tracking-tight">{children}</span>
    </span>
  )
}

/**
 * StatusBadge - Specialized badge for status indicators
 */
type StatusType = 'success' | 'warning' | 'error' | 'info' | 'pending' | 'inactive' | 'neutral' | 'active'

interface StatusBadgeProps {
  status: StatusType
  label?: string
  children?: ReactNode
  showDot?: boolean
  size?: BadgeSize
  className?: string
}

const statusConfig: Record<StatusType, { variant: BadgeVariant; label: string }> = {
  success: { variant: 'green', label: 'Success' },
  warning: { variant: 'orange', label: 'Warning' },
  error: { variant: 'red', label: 'Error' },
  info: { variant: 'blue', label: 'Info' },
  pending: { variant: 'yellow', label: 'Pending' },
  inactive: { variant: 'default', label: 'Inactive' },
  neutral: { variant: 'default', label: 'Neutral' },
  active: { variant: 'green', label: 'Active' },
}

export function StatusBadge({
  status,
  label,
  children,
  showDot = true,
  size = 'md',
  className,
}: StatusBadgeProps) {
  const config = statusConfig[status]

  return (
    <Badge
      variant={config.variant}
      size={size}
      dot={showDot}
      className={className}
    >
      {children || label || config.label}
    </Badge>
  )
}

/**
 * NotificationBadge - Small numeric indicator badge
 */
interface NotificationBadgeProps {
  count: number
  max?: number
  className?: string
}

export function NotificationBadge({
  count,
  max = 99,
  className,
}: NotificationBadgeProps) {
  if (count <= 0) return null

  const displayCount = count > max ? `${max}+` : count.toString()

  return (
    <span
      className={cn(
        'liquid-notification-badge',
        className
      )}
    >
      {displayCount}
    </span>
  )
}

/**
 * CountBadge - Badge showing a count with label
 */
interface CountBadgeProps {
  count: number
  label: string
  variant?: BadgeVariant
  size?: BadgeSize
  className?: string
}

export function CountBadge({
  count,
  label,
  variant = 'blue',
  size = 'md',
  className,
}: CountBadgeProps) {
  return (
    <Badge
      variant={variant}
      size={size}
      className={className}
    >
      <span className="font-bold">{count}</span>
      <span className="opacity-80">{label}</span>
    </Badge>
  )
}
